import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import {
    Heart, AlertTriangle, GraduationCap, IndianRupee, HeartHandshake,
    Search, ChevronRight, Info, ShieldCheck, Clock,
} from "lucide-react";

const CATEGORY_META = {
    "Healthy":              { tone: "emerald", icon: Heart },
    "Needs Attention":      { tone: "amber",   icon: AlertTriangle },
    "Onboarding Required":  { tone: "blue",    icon: GraduationCap },
    "Billing Attention":    { tone: "rose",    icon: IndianRupee },
    "Support Attention":    { tone: "violet",  icon: HeartHandshake },
};

const TONE_CLS = {
    emerald: "bg-emerald-50 text-emerald-700 border-emerald-200",
    amber:   "bg-amber-50 text-amber-800 border-amber-200",
    blue:    "bg-blue-50 text-blue-700 border-blue-200",
    rose:    "bg-rose-50 text-rose-700 border-rose-200",
    violet:  "bg-violet-50 text-violet-700 border-violet-200",
    stone:   "bg-stone-100 text-stone-700 border-stone-200",
};

function CategoryCard({ label, count, onClick, active }) {
    const meta = CATEGORY_META[label];
    const Icon = meta?.icon || Info;
    return (
        <button
            onClick={onClick}
            data-testid={`cs-cat-${label.toLowerCase().replace(/\s+/g,"-")}`}
            className={`rounded-2xl border p-4 text-left transition-all hover:shadow-md ${TONE_CLS[meta?.tone || "stone"]} ${active ? "ring-2 ring-offset-2 ring-stone-900" : ""}`}
        >
            <div className="flex items-center gap-2 mb-2 opacity-80">
                <Icon size={13} />
                <span className="text-[10px] font-bold uppercase tracking-widest">{label}</span>
            </div>
            <div className="text-3xl font-semibold tabular-nums">{count}</div>
        </button>
    );
}

function HealthBar({ score }) {
    const tone = score >= 75 ? "bg-emerald-500" : score >= 55 ? "bg-amber-500" : "bg-rose-500";
    return (
        <div className="w-full h-1.5 rounded-full bg-stone-100 overflow-hidden">
            <div className={`h-full ${tone}`} style={{ width: `${score}%` }} />
        </div>
    );
}

function WhyThisScore({ factors, onClose }) {
    return (
        <div className="mt-2 rounded-xl border border-stone-200 bg-stone-50 p-3" data-testid="why-this-score">
            <div className="flex items-center justify-between mb-2">
                <span className="text-[11px] font-bold uppercase tracking-widest text-stone-600">Why this score?</span>
                <button onClick={onClose} className="text-[11px] text-stone-500 hover:text-stone-900">Close</button>
            </div>
            <ul className="space-y-1.5">
                {factors.map((f, i) => (
                    <li key={i} className="flex items-start gap-2 text-[12px]">
                        <span className={`inline-block w-10 shrink-0 text-right tabular-nums font-semibold ${f.delta >= 0 ? "text-emerald-700" : "text-rose-700"}`}>
                            {f.delta > 0 ? `+${f.delta}` : f.delta}
                        </span>
                        <span className="text-stone-700">{f.detail}</span>
                    </li>
                ))}
            </ul>
            <div className="mt-3 pt-2 border-t border-stone-200 text-[10.5px] text-stone-500 flex items-center gap-1.5">
                <ShieldCheck size={11} /> Health score uses SaaS engagement metadata only. No financial values are read.
            </div>
        </div>
    );
}

export default function SuperAdminCustomerSuccess() {
    const [data, setData] = useState(null);
    const [cat, setCat] = useState(null);
    const [q, setQ] = useState("");
    const [expanded, setExpanded] = useState({});

    const load = () => {
        const params = new URLSearchParams();
        if (cat) params.set("category", cat);
        if (q) params.set("q", q);
        api.get(`/super-admin/customer-success?${params.toString()}`)
            .then((r) => setData(r.data))
            .catch(() => setData({ rows: [], counts_by_category: {}, total: 0 }));
    };

    useEffect(() => { load(); }, [cat]);

    if (!data) return <div className="p-6 text-stone-500">Loading customer success…</div>;

    const totals = data.counts_by_category || {};

    return (
        <div className="max-w-[1400px] mx-auto" data-testid="customer-success-page">
            <div className="mb-6">
                <div className="eyebrow text-amber-700">Customer Success</div>
                <h1 className="font-display text-4xl sm:text-5xl text-stone-900 leading-none mt-2">
                    Retention Intelligence
                </h1>
                <p className="text-sm text-stone-500 mt-3 max-w-2xl">
                    Health scores derived exclusively from SaaS engagement metadata. Financial values are never used.
                </p>
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3" data-testid="cs-category-grid">
                {Object.keys(CATEGORY_META).map((label) => (
                    <CategoryCard
                        key={label} label={label} count={totals[label] || 0}
                        active={cat === label}
                        onClick={() => setCat(cat === label ? null : label)}
                    />
                ))}
            </div>

            <div className="mt-6 flex flex-wrap items-center gap-3">
                <div className="relative flex-1 min-w-[240px]">
                    <Search size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-stone-400" />
                    <input
                        value={q}
                        onChange={(e) => setQ(e.target.value)}
                        onKeyDown={(e) => { if (e.key === "Enter") load(); }}
                        placeholder="Search by workspace name or email…"
                        data-testid="cs-search"
                        className="w-full pl-9 pr-3 py-2 rounded-full border border-stone-200 text-sm focus:outline-none focus:border-stone-400"
                    />
                </div>
                {cat && (
                    <button onClick={() => setCat(null)}
                            data-testid="cs-clear-filter"
                            className="text-[12px] text-stone-500 hover:text-stone-900">
                        Clear filter
                    </button>
                )}
                <div className="text-[11px] text-stone-500 uppercase tracking-widest">
                    {data.total} customer{data.total === 1 ? "" : "s"}
                </div>
            </div>

            <div className="mt-4 rounded-2xl border border-stone-200 bg-white overflow-hidden" data-testid="cs-list">
                {data.rows.length === 0 ? (
                    <div className="p-10 text-center text-stone-500 text-sm">
                        No customers match this view.
                    </div>
                ) : (
                    data.rows.map((r) => (
                        <div key={r.tenant_id} className="border-b border-stone-100 last:border-b-0" data-testid={`cs-row-${r.tenant_id}`}>
                            <button
                                onClick={() => setExpanded((s) => ({ ...s, [r.tenant_id]: !s[r.tenant_id] }))}
                                className="w-full text-left px-5 py-4 flex items-center gap-4 hover:bg-stone-50 transition-colors"
                            >
                                <div className="flex-1 min-w-0">
                                    <div className="flex items-center gap-2">
                                        <span className="text-[14px] font-semibold text-stone-900 truncate">
                                            {r.name || "Unnamed workspace"}
                                        </span>
                                        <span className={`text-[10px] uppercase tracking-widest px-2 py-0.5 rounded border ${TONE_CLS[CATEGORY_META[r.category]?.tone || "stone"]}`}>
                                            {r.category}
                                        </span>
                                    </div>
                                    <div className="text-[11.5px] text-stone-500 mt-1 flex items-center gap-3 flex-wrap">
                                        <span>{r.admin_email || "—"}</span>
                                        <span>· {r.country}</span>
                                        <span>· {r.plan}</span>
                                        <span>· {r.status}</span>
                                        {r.signals?.last_login_at && (
                                            <span className="inline-flex items-center gap-1">
                                                <Clock size={10} />
                                                Last active {new Date(r.signals.last_login_at).toLocaleDateString()}
                                            </span>
                                        )}
                                    </div>
                                </div>
                                <div className="w-40 shrink-0">
                                    <div className="flex items-center justify-end gap-2 mb-1">
                                        <span className="text-2xl font-semibold tabular-nums">{r.score}</span>
                                        <span className="text-[10px] text-stone-500 uppercase tracking-widest">/100</span>
                                    </div>
                                    <HealthBar score={r.score} />
                                </div>
                                <ChevronRight size={14} className={`text-stone-400 shrink-0 transition-transform ${expanded[r.tenant_id] ? "rotate-90" : ""}`} />
                            </button>
                            {expanded[r.tenant_id] && (
                                <div className="px-5 pb-5">
                                    <WhyThisScore factors={r.factors} onClose={() => setExpanded((s) => ({ ...s, [r.tenant_id]: false }))} />
                                    {r.signals?.modules_missing?.length > 0 && (
                                        <div className="mt-3 text-[12px] text-stone-600">
                                            <span className="font-semibold">Modules not yet adopted:</span>{" "}
                                            {r.signals.modules_missing.join(", ")}
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                    ))
                )}
            </div>
        </div>
    );
}
